import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useLazyQuery } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { Card, Spin, Alert, Table, Dropdown, Menu } from 'antd'
import { CalculatorOutlined, DownOutlined } from '@ant-design/icons'
import { GET_ORDER_RECOMMENDED_SHIPPINGS } from '../../graphql/orderRecommendedShippings'

const SHIPPING_TYPES = [
  { key: 'full_truck_load', i18nKey: 'inquiries:shippingCost:fullTruckLoad' },
  { key: 'less_truck_load', i18nKey: 'inquiries:shippingCost:lessTruckLoad' },
  { key: 'express_delivery', i18nKey: 'inquiries:shippingCost:expressDelivery' }
]

const OrderRecommendedShippings = ({ orderNumber, i18n }) => {
  const [hasRequested, setHasRequested] = useState(false)
  const [selectedType, setSelectedType] = useState(SHIPPING_TYPES[0])
  const [fetchRecommendedShippings, { data, loading, error }] = useLazyQuery(
    GET_ORDER_RECOMMENDED_SHIPPINGS,
    {
      variables: { orderNumber, shippingType: selectedType.key }
    }
  )

  const handleRequestRecommendations = () => {
    setHasRequested(true)
    fetchRecommendedShippings()
  }

  const handleMenuClick = ({ key }) => {
    const type = SHIPPING_TYPES.find(t => t.key === key)
    setSelectedType(type)
    setHasRequested(true)
    fetchRecommendedShippings({ variables: { orderNumber, shippingType: key } })
  }

  const menu = (
    <Menu onClick={handleMenuClick}>
      {SHIPPING_TYPES.map(type => (
        <Menu.Item key={type.key}>
          {i18n.t(type.i18nKey)}
        </Menu.Item>
      ))}
    </Menu>
  )

  // Show button if user hasn't requested data yet
  if (!hasRequested) {
    return (
      <Card title={i18n.t('inquiries:shippingCost:recommendedShippings')} size="small">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Dropdown.Button
            type="primary"
            icon={<DownOutlined />}
            overlay={menu}
            onClick={handleRequestRecommendations}
            disabled={!orderNumber}
          >
            <CalculatorOutlined /> {i18n.t('inquiries:shippingCost:requestRecommendation', { type: i18n.t(selectedType.i18nKey) })}
          </Dropdown.Button>
        </div>
      </Card>
    )
  }

  if (loading) {
    return (
      <Card title={i18n.t('inquiries:shippingCost:recommendedShippings')} size="small">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card title={i18n.t('inquiries:shippingCost:recommendedShippings')} size="small">
        <div style={{ marginBottom: 12 }}>
          <Dropdown.Button
            type="primary"
            icon={<DownOutlined />}
            overlay={menu}
            onClick={handleRequestRecommendations}
          >
            <CalculatorOutlined /> {i18n.t('inquiries:shippingCost:requestRecommendation', { type: i18n.t(selectedType.i18nKey) })}
          </Dropdown.Button>
        </div>
        <Alert
          message={i18n.t('inquiries:error')}
          description={error.message}
          type="error"
          showIcon
        />
      </Card>
    )
  }

  const recommendedShippings = (data && data.order && data.order.recommendedShippings) || []

  if (recommendedShippings.length === 0) {
    return (
      <Card title={i18n.t('inquiries:shippingCost:recommendedShippings')} size="small">
        <div style={{ marginBottom: 12 }}>
          <Dropdown.Button
            type="primary"
            icon={<DownOutlined />}
            overlay={menu}
            onClick={handleRequestRecommendations}
          >
            <CalculatorOutlined /> {i18n.t('inquiries:shippingCost:requestRecommendation', { type: i18n.t(selectedType.i18nKey) })}
          </Dropdown.Button>
        </div>
        <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
          {i18n.t('inquiries:noRecommendedShippings')}
        </div>
      </Card>
    )
  }

  const columns = [
    {
      title: i18n.t('inquiries:shippingCost:vehicleType'),
      dataIndex: ['vehicleType', 'name'],
      key: 'vehicleType',
    },
    {
      title: i18n.t('inquiries:shippingCost:weightLimit'),
      dataIndex: ['vehicleType', 'weightLimit'],
      key: 'weightLimit',
      render: (weightLimit) => weightLimit ? `${weightLimit} kg` : '-'
    },
    {
      title: i18n.t('inquiries:quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: i18n.t('inquiries:totalPrice'),
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (totalPrice) => totalPrice ? `${totalPrice.toLocaleString()} ${i18n.t('baht')}` : '-'
    },
  ]

  return (
    <Card title={i18n.t('inquiries:shippingCost:recommendedShippings')} size="small">
      <div style={{ marginBottom: 12 }}>
        <Dropdown.Button
          type="primary"
          icon={<DownOutlined />}
          overlay={menu}
          onClick={handleRequestRecommendations}
          loading={loading}
        >
          <CalculatorOutlined /> {i18n.t(selectedType.i18nKey)}
        </Dropdown.Button>
      </div>
      <Table
        columns={columns}
        dataSource={recommendedShippings}
        rowKey={(record) => record.id}
        pagination={false}
        size="small"
      />
    </Card>
  )
}

OrderRecommendedShippings.propTypes = {
  orderNumber: PropTypes.string.isRequired,
  i18n: PropTypes.object.isRequired
}

export default withTranslation('inquiries')(OrderRecommendedShippings)
